import React, { useEffect, useRef, useState } from "react";
import { Text, Sphere, RoundedBox } from '@react-three/drei';
import { Mesh, Vector3, Quaternion, Group } from "three";
import { useModelPropertiesStore } from "../../store/modelPropertiesStore/modelPropertiesStore";
import LayerMappingsEnum from "../../enums/LayerMappingsEnum";
import { sceneUnitsFromMm } from "../../utils/scaler";
import { RenderOrderEnum } from "../../enums/RenderOrderEnum";

import { ThreeEvent } from '@react-three/fiber';

interface NameLabelComponentProps {
  text: string;
  borderRadius?: number;
  color?: string;
  textColor?: string;
  fontSize?: number,
  position: Vector3;
  onDoubleClick?: (event: ThreeEvent<MouseEvent>) => void;
  tooltip?: string;
}

const NameLabelComponent = ({
  text = "Default Text",
  borderRadius = 0.05,
  color = "white",
  textColor = "darkslategray",
  position = new Vector3(0, 0, 0),
  fontSize = 0.15,
  onDoubleClick = undefined,
  tooltip = undefined
}: NameLabelComponentProps) => {
  const groupRef = useRef<Group>(null);
  const textRef = useRef<Text>(null);
  const boxRef = useRef<Mesh>(null);
  const {rotationOffset, boundingBox} = useModelPropertiesStore();
  const [boxDimensions, setBoxDimensions] = useState({width: 2, height: 0.3}); 

  const getBaseQuaternion = (group: Group) => {

    if (!group.parent)
      return; 
    
    const parentWorldQuat = new Quaternion();
    
    group.parent?.getWorldQuaternion(parentWorldQuat); // Get parent world rotation
      
    // Invert the parent's rotation to neutralize it
    const inverseParentRotation = parentWorldQuat.clone().invert();

    // Apply this inverse rotation to the text to keep it aligned with the world X-axis
    group.setRotationFromQuaternion(inverseParentRotation);
  };
  
  useEffect(() => {
    
    // Rotate label to match storeys orientation 
    if (groupRef.current)
      {
      getBaseQuaternion(groupRef.current);
      //setBaseQuaternion(groupRef.current.quaternion);
    }

  }, []);

useEffect(() =>  {
  if (groupRef.current)
  {
    getBaseQuaternion(groupRef.current);
    groupRef.current.rotateZ(rotationOffset);
  }
}, [rotationOffset])

useEffect(() =>  {
  if (groupRef.current)
  {
    groupRef.current.position.setComponent(2, boundingBox?.max.z ?? sceneUnitsFromMm(3000) )
  }
}, [boundingBox])

  
  return (
    <group 
      ref={groupRef}
      position={position} 
      layers={LayerMappingsEnum.RoomNameLabels}
      renderOrder={RenderOrderEnum.RoomNames}
      onDoubleClick={onDoubleClick}
      
      >

      {/* Helper to visualize position */}
      {/* <Sphere scale={0.1} /> */}

     <RoundedBox 
      ref={boxRef}
      args={[boxDimensions.width, boxDimensions.height, 0.001]} // Width, height, depth
      radius={borderRadius} // Corner radius
      smoothness={4} // Smooth edges
      layers={LayerMappingsEnum.RoomNameLabels}
      renderOrder={RenderOrderEnum.RoomNames}
            
        >
          <meshBasicMaterial color={color} transparent={true} opacity={0.7} depthTest={false} />
      </RoundedBox>
      
      {/* Text */}
     <Text
      ref={textRef}
      position={[0,0,0.002]}
      fontSize={fontSize}
      color={textColor}
      anchorX="center" // Align text to the center horizontally
      anchorY="middle" // Align text to the center vertically
      layers={LayerMappingsEnum.RoomNameLabels}
      renderOrder={RenderOrderEnum.RoomNames}
      
      onSync={(textMesh) => {
        setBoxDimensions( { width: textMesh.geometry.boundingBox.max.x - textMesh.geometry.boundingBox.min.x+0.2,
                             height: textMesh.geometry.boundingBox.max.y - textMesh.geometry.boundingBox.min.y} );
        
      }}>
        <meshBasicMaterial depthTest={false} color={textColor} />
      {text}
    </Text>

    </group>

  );
};

export default NameLabelComponent;
